import { type ContentHref, type Facet, type WebPage, webFacetTypes } from '@generalTypes/apiTypes';
import { selectRawWebPage, selectWebPage } from '@newStore/documentApi/documentApiSelectors';
import { htmlDiffFunction } from '@newStore/documentUI/transformProposal/asideDiffText';
import { selectWebTemplates } from '@newStore/externalData/externalDataSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import type { ReferenceFrameState } from '@newStore/referenceFrames/referenceFramesState';
import type { AsideChangeMessageSelector, EditAsideWebFacets } from '@nodeTypeConfig/configTypes';
import { selectCurrentWebConfig } from '@store/selectors/asideSelectors';

export type WebFacetVmItem = { facet: Facet; sourceName?: string | null };
export type WebFacetVm = Array<WebFacetVmItem>;

const facetMapper = (
  webConfig: WebPage | undefined,
  referenceFrames: ReferenceFrameState,
): WebFacetVm => {
  if (!webConfig) {
    return [];
  }

  const facets = webConfig.options?.facets || [];

  return facets.map((facet) => {
    if (facet.component === webFacetTypes.SELECT_FROM_REFERENCE_FRAME) {
      const referenceFrameRoot =
        referenceFrames[facet.source?.href as ContentHref]?.content[
          facet.source?.href as ContentHref
        ];
      return {
        facet,
        sourceName: referenceFrameRoot?.title || null,
      };
    }
    return {
      facet,
    };
  });
};

export const selectAsideWebFacetComponentOptions = createTypedSelector(
  [(state) => selectCurrentWebConfig(state), (state) => selectWebTemplates(state)],
  (webConfig, templates) => {
    if (!webConfig) {
      return [];
    }
    return (templates[webConfig.template.href].options || []).map((option) => ({
      key: option,
      label: option,
    }));
  },
);

export const selectAsideWebFacets = createTypedSelector(
  [(state, href: ContentHref) => selectWebPage(state, href), (state) => state.referenceFrames],
  (webConfig, referenceFrames) => {
    return facetMapper(webConfig, referenceFrames);
  },
);

const selectRawAsideWebFacets = createTypedSelector(
  [(state, href: ContentHref) => selectRawWebPage(state, href), (state) => state.referenceFrames],
  (webConfig, referenceFrames) => {
    return facetMapper(webConfig, referenceFrames);
  },
);

const webFacetsToString = (webFacets: WebFacetVm): string[] => {
  return webFacets.map(
    (z) => `
  Label: ${z.facet.label} <br />
  Component: ${z.facet.component} <br />
  ${'source' in z.facet ? `Source: ${z.sourceName} <br />` : ''}
`,
  );
};

export const selectChangeMessageForWebFacets: AsideChangeMessageSelector<EditAsideWebFacets> =
  createTypedSelector(
    [
      (state, href: ContentHref) => selectAsideWebFacets(state, href),
      (state, href: ContentHref) => selectRawAsideWebFacets(state, href),
    ],
    (webFacets, rawWebFacets): string | null => {
      const newValue = webFacetsToString(webFacets).join('<br />');
      const oldValue = webFacetsToString(rawWebFacets).join('<br />');

      if (newValue === oldValue) {
        return null;
      }

      const diff = htmlDiffFunction(newValue, oldValue);

      return diff;
    },
  );
